import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import ShadowBanner from '../overview/shadow_banner';
import TeacherFilter from '../shared/teacher_filter';
import CloseMonthModal from './close_month_modal';
import DailyReport from './daily_report';
import IssueTable from './issue_table';
import ReopenModal from './reopen_modal';
import RunPanel from './month_run/run_panel';
import TierSummary from './tiers';
import useMonthRun from './month_run/use_month_run';
import { fetch_needs_attention_summary } from '../api';
import { accept_month_choice } from '../overview/params';
import { error_code_text, summary_view } from './adapters';
import {
  NO_RETRY_TEXT,
  RUNS_LOAD_FAILED_TEXT,
  RUN_LOAD_FAILED_TEXT,
  run_busy_text,
  run_settle_note
} from './month_run/adapters';
import { DECIDED_STATUS_FILTER, VIEW_DAILY, VIEW_MONTHLY } from './params';
import { BUCKET_NEEDS_ATTENTION, RUN_KIND_REOPEN, retry_rows } from './month_run/params';
import { ist_day } from '../shared/ist';

/*
 * PROD-7828 R5b — "Needs attention": one monthly triage view for Ops.
 * Two distinct reports, because the daily Slack digest deep-links to each (§4.8):
 *   MONTHLY — the running state of the month: seven tiers, the readiness header, close and reopen.
 *   DAILY   — what is new or cleared since the previous post.
 * Everything rendered here comes from needs_attention/adapters.js; no number is derived in the JSX.
 * D110: close and reopen are server-side runs; the month's active run is picked up on mount (so a
 * reload resumes it) and shown above the tiers, with Close/Reopen off until it ends.
 * months / on_month: the shell's month list and setter, so the daily report's monthly link can switch
 * the month. on_changed: a close, reopen or issue action landed; the shell re-reads its badges (HL5).
 * now: the shell's clock, which decides "today" for the daily report.
 * switch_rev: bumped when the header flips month close; the summary re-reads close_enabled.
 * on_summary_loaded: a summary read landed; the shell re-reads the header's month-close chip.
 */
const system_now = () => new Date();

// D110 §6: while a run is active the summary behind it is re-read this often.
export const RUN_SUMMARY_REFRESH_MS = 30000;

const NeedsAttentionTab = ({
  pay_month,
  month_label,
  deep_link = {},
  on_deep_link_consumed,
  months = [],
  on_month,
  on_changed,
  on_error,
  now = system_now,
  switch_rev = 0,
  on_summary_loaded
}) => {
  const history = useHistory();
  const location = useLocation();
  const [view, setView] = useState(deep_link.view === VIEW_DAILY ? VIEW_DAILY : VIEW_MONTHLY);
  // The issue list open, and the month it was opened for: under any other month it is not rendered, so
  // a month switch never fetches (new month, old tier) (R6H-L6). `status` is D80's "See all" filter;
  // `teacher_uuid`/`teacher_name` are the shared Teacher filter's pick, or a run row's "View issues"
  // (no name there — the list backfills it from a loaded row). The digest's tier belongs to the
  // digest's month; the shell may have fallen back to another one.
  const [drill, setDrill] = useState({
    pay_month,
    tier: !deep_link.pay_month || deep_link.pay_month === pay_month ? deep_link.tier || '' : '',
    status: '',
    teacher_uuid:
      !deep_link.pay_month || deep_link.pay_month === pay_month ? deep_link.teacher_uuid || '' : '',
    teacher_name: ''
  });
  const open_list = (
    tier_value,
    status_value = '',
    month = pay_month,
    teacher_uuid = '',
    teacher_name = ''
  ) =>
    setDrill({
      pay_month: month,
      tier: tier_value || '',
      status: status_value,
      teacher_uuid,
      teacher_name
    });
  const drill_here = drill.pay_month === pay_month;
  const tier = drill_here ? drill.tier : '';
  const list_status = drill_here ? drill.status : '';
  const list_teacher = drill_here ? drill.teacher_uuid : '';
  const list_teacher_name = drill_here ? drill.teacher_name : '';

  // The Teacher filter mirrors itself into the URL (merged with whatever else is there) so a reload or
  // a shared link keeps it, the same as a run row's "View issues" deep link.
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const current = qs.get('teacher_uuid') || '';
    const next = list_teacher || '';
    if (current === next) return;
    if (next) qs.set('teacher_uuid', next);
    else qs.delete('teacher_uuid');
    const search = qs.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list_teacher]);
  const [day, setDay] = useState(() => deep_link.day || ist_day(now()));
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(summary_view(null));
  // A failed load leaves an empty summary; the panel must not read that as "no decisions".
  const [load_failed, setLoadFailed] = useState(false);
  const [close_open, setCloseOpen] = useState(false);
  const [reopen_open, setReopenOpen] = useState(false);
  // "Retry the teachers that failed": the teachers and reason the dialog opens with.
  const [retry_seed, setRetrySeed] = useState(null);
  // D74(a): set after a close that wrote something; cleared by the Refresh it offers or a month change.
  const [settle_note, setSettleNote] = useState('');

  // The shell toasts the server's message; a code with its own sentence replaces it (BAD_BODY, BAD_QUERY).
  const report_error = useCallback(
    (e, fallback) => {
      const specific = error_code_text(e);
      on_error(specific ? {} : e, specific || fallback);
    },
    [on_error]
  );

  const request_seq = useRef(0);
  const summary_loaded_ref = useRef(on_summary_loaded);
  summary_loaded_ref.current = on_summary_loaded;
  const prev_month = useRef(pay_month);

  // quiet: the periodic refresh behind a run. It only ever replaces the summary with a newer one: no
  // loading flag, and a failure keeps the page as it was, without a toast every 30 s.
  const load_summary = useCallback(
    (quiet) => {
      const silent = quiet === true;
      request_seq.current += 1;
      const seq = request_seq.current;
      if (!pay_month) {
        setSummary(summary_view(null));
        setLoadFailed(false);
        setLoading(false);
        return;
      }
      if (!silent) {
        setLoading(true);
        setLoadFailed(false);
      }
      fetch_needs_attention_summary(pay_month)
        .then((data) => {
          if (seq !== request_seq.current) return;
          setSummary(summary_view(data));
          setLoadFailed(false);
          setLoading(false);
          if (summary_loaded_ref.current) summary_loaded_ref.current();
        })
        .catch((e) => {
          if (seq !== request_seq.current || silent) return;
          report_error(e, 'Failed to load the needs-attention summary.');
          setSummary(summary_view(null));
          setLoadFailed(true);
          setLoading(false);
        });
    },
    [pay_month, report_error]
  );

  useEffect(() => {
    load_summary();
  }, [load_summary]);

  // The header flipped month close: the Close/Reopen buttons follow it without a page reload.
  useEffect(() => {
    if (switch_rev > 0) load_summary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switch_rev]);

  useEffect(() => {
    if (on_deep_link_consumed) on_deep_link_consumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A month CHANGE drops the settle note of the month left behind. Keyed off the last month seen, not
  // mount (HI1). A drill-down of another month is already hidden; it is dropped so it can't come back.
  useEffect(() => {
    if (prev_month.current === pay_month) return;
    prev_month.current = pay_month;
    setDrill((d) =>
      d.pay_month === pay_month
        ? d
        : { pay_month, tier: '', status: '', teacher_uuid: '', teacher_name: '' }
    );
    setSettleNote('');
  }, [pay_month]);

  const changed = () => {
    load_summary();
    if (on_changed) on_changed();
  };

  const month_run = useMonthRun({
    pay_month,
    on_finished: (run) => {
      setSettleNote(run_settle_note(run));
      changed();
    }
  });
  const run_active = month_run.active;

  useEffect(() => {
    if (!run_active) return undefined;
    const timer = setInterval(() => load_summary(true), RUN_SUMMARY_REFRESH_MS);
    return () => clearInterval(timer);
  }, [run_active, load_summary]);

  const show_run = (run_uuid, run) => {
    setCloseOpen(false);
    setReopenOpen(false);
    setRetrySeed(null);
    setView(VIEW_MONTHLY);
    open_list('');
    month_run.adopt(run_uuid, run);
  };

  // A new run over the retryable teachers of the one shown (B8); '' once its dialog is open.
  const retry_failed = () => {
    const run = month_run.run;
    return month_run.load_all_items(BUCKET_NEEDS_ATTENTION).then((items) => {
      const rows = retry_rows(items);
      if (!rows.length) return NO_RETRY_TEXT;
      setRetrySeed({ token: Date.now(), rows, reason: run.reason, run_uuid: run.run_uuid });
      if (run.kind === RUN_KIND_REOPEN) setReopenOpen(true);
      else setCloseOpen(true);
      return '';
    });
  };

  const can_open_month = (m) => accept_month_choice(m, months);

  // HM4: the daily report's link names a month; follow it rather than staying on the shell's month.
  const open_monthly = (target) => {
    setView(VIEW_MONTHLY);
    if (target.pay_month && target.pay_month !== pay_month && can_open_month(target.pay_month)) {
      open_list(target.tier, '', target.pay_month);
      on_month(target.pay_month);
      return;
    }
    open_list(target.tier);
  };

  if (!pay_month) return null;

  const show_list = !!tier || !!list_status || !!list_teacher;
  // Only a summary of another month (or none) is replaced by the spinner; a refresh keeps the page, and
  // with it the button that opened a dialog, mounted — so focus can return to it (HL6).
  const first_load = loading && summary.pay_month !== pay_month;

  return (
    <div>
      <div className="tp-seg">
        <button
          type="button"
          className={view === VIEW_MONTHLY ? 'on' : ''}
          aria-pressed={view === VIEW_MONTHLY}
          onClick={() => setView(VIEW_MONTHLY)}
        >
          Monthly report
        </button>
        <button
          type="button"
          className={view === VIEW_DAILY ? 'on' : ''}
          aria-pressed={view === VIEW_DAILY}
          onClick={() => setView(VIEW_DAILY)}
        >
          Daily report
        </button>
      </div>

      {view === VIEW_DAILY ? (
        <DailyReport
          day={day}
          now={now}
          on_day={setDay}
          on_error={report_error}
          can_open_month={can_open_month}
          on_open_monthly={open_monthly}
        />
      ) : (
        <div>
          <ShadowBanner show={summary.shadow_banner} />
          {!first_load ? (
            <div className="tp-frow" style={{ marginBottom: 14 }}>
              <div style={{ width: '280px' }}>
                <TeacherFilter
                  id="tp-na-teacher-filter"
                  value={{
                    teacher_uuid: list_teacher,
                    display: list_teacher
                      ? list_teacher_name || `Teacher ${list_teacher.slice(-8)}`
                      : ''
                  }}
                  onChange={(next) =>
                    next.teacher_uuid
                      ? open_list('', '', pay_month, next.teacher_uuid, next.display)
                      : open_list('')
                  }
                />
              </div>
            </div>
          ) : null}
          {!show_list && first_load ? <CustomLoading /> : null}
          {!show_list && month_run.load_failed === 'runs' ? (
            <LoadFailed text={RUNS_LOAD_FAILED_TEXT} on_retry={month_run.reload} />
          ) : null}
          {!show_list && month_run.load_failed === 'run' && !month_run.run ? (
            <LoadFailed text={RUN_LOAD_FAILED_TEXT} on_retry={month_run.reload} />
          ) : null}
          {!show_list && month_run.run ? (
            <RunPanel
              run={month_run.run}
              bucket={month_run.bucket}
              offset={month_run.offset}
              items_loading={month_run.items_loading}
              poll_failed={month_run.poll_failed || month_run.load_failed === 'run'}
              action_busy={month_run.action_busy}
              action_error={month_run.action_error}
              focus_token={month_run.focus_token}
              focused_token={month_run.focused_token}
              on_focused={month_run.on_focused}
              on_bucket={month_run.set_bucket}
              on_page={month_run.set_page}
              on_cancel={month_run.cancel}
              on_resume={month_run.resume}
              on_retry={retry_failed}
              on_view_issues={(t, teacher_uuid) => open_list(t, '', pay_month, teacher_uuid)}
              on_dismiss={month_run.dismiss}
            />
          ) : null}
          {show_list ? (
            <IssueTable
              key={`${pay_month}|${tier}|${list_status}|${list_teacher}`}
              pay_month={pay_month}
              tier={tier}
              teacher_uuid={list_teacher || undefined}
              initial_status={list_status}
              on_error={report_error}
              on_changed={on_changed}
              on_back={() => {
                open_list('');
                load_summary();
              }}
            />
          ) : null}
          {!show_list && !first_load ? (
            <TierSummary
              summary={summary}
              load_failed={load_failed}
              month_label={month_label}
              on_select_tier={(t) => open_list(t)}
              on_see_decisions={() => open_list('', DECIDED_STATUS_FILTER)}
              on_close_month={() => setCloseOpen(true)}
              on_reopen={() => setReopenOpen(true)}
              run_busy_text={run_busy_text(month_run.run, month_label)}
              settle_note={settle_note}
              on_refresh={() => {
                setSettleNote('');
                load_summary();
              }}
              on_retry={load_summary}
            />
          ) : null}
        </div>
      )}

      <CloseMonthModal
        show={close_open}
        pay_month={pay_month}
        month_label={month_label}
        seed={close_open ? retry_seed : null}
        on_close={() => {
          setCloseOpen(false);
          setRetrySeed(null);
        }}
        on_started={show_run}
        on_show_run={(run_uuid) => show_run(run_uuid, null)}
        on_error={report_error}
      />
      <ReopenModal
        show={reopen_open}
        pay_month={pay_month}
        month_label={month_label}
        seed={reopen_open ? retry_seed : null}
        on_close={() => {
          setReopenOpen(false);
          setRetrySeed(null);
        }}
        on_started={show_run}
        on_show_run={(run_uuid) => show_run(run_uuid, null)}
        on_error={report_error}
      />
    </div>
  );
};

export default NeedsAttentionTab;
